import React, { useContext, useEffect, useRef, useState } from 'react';

import axios from 'axios';

import { animated, useSpring } from 'react-spring';

import { AppContext } from '~/contexts/AppContext';

import SingleChoiceAnswer from './answers/SingleChoiceAnswer';
import MultipleChoiceAnswer from './answers/MultipleChoiceAnswer';
import OpenEndedAnswer from './answers/OpenEndedAnswer';
import DropdownAnswer from './answers/DropdownAnswer';
import IframeAnswer from './answers/IframeAnswer';
import RequestLocation from './answers/LocationRequest';

import { Answer, Question } from '~/types';
import StyledContentPanel from '../../components/StyledContentPanel';
import { useTranslation } from 'react-i18next';
import TypingTextV2 from '../../components/TypingTextV2';
import DisplayMedia from './DisplayMedia';
import { useParams } from 'react-router-dom';

import { fixBold } from '~/util/util';

import StyledButton from '~/components/StyledButton';

import * as topicUtils from '../../util/topic';
import { QuestionContext, SubmitAnswerFunction } from '~/contexts/QuestionContext';
import MatomoTracker from '~/util/matomo/MatomoTracker';

export type RatingEndFunction = (ratingEnd: boolean) => void;

interface QuestionAndAnswerProps {
    ratingValue: number | null;
    ratingId: number | null;
    setRatingEnd: RatingEndFunction;
    firstQuestionAnswered: () => void;
}

enum QnAAnimationState {
    ContentPanelFadeIn = 'content-panel-fade-in',
    //TypeInfoClient = 'type-info-client',
    TypeTitleText = 'type-title-text',
    MediaFadeIn = 'media-fade-in',
    MediaPlay = 'media-play',
    TypeSubtitleText='type-subtitle-text',
    AnswersFadeIn = 'answers-fade-in',
    QuestionFadeOut = 'question-fade-out'
}

interface QuestionAndAnswerState {
    qnaAnimationState: QnAAnimationState;
    questionIndex: number | undefined;
    questionTime: number;
}

const QuestionAndAnswer: React.FC<QuestionAndAnswerProps> = (props) => {

    const { apiUrl, topicData, geolocationConsent, preciseCoordinates } = useContext(AppContext)!;

    const { i18n } = useTranslation();
    const { t } = useTranslation();
    const params: { topicHash: string } = useParams();

    const [qnaContainerStyle, qnaContainerApi] = useSpring(() => ({
        opacity: 0,
    }));

    const [questionMediaStyle, questionMediaApi] = useSpring(() => ({
        opacity: 0,
    }));

    const [dropdownAnswerStyle, dropdownAnswerApi] = useSpring(() => ({
        opacity: 0,
    }));

    const getQuestionIndex = (questionId: number) => {
        let questionIndex = 0;

        if(!topicData)
            return -1;

        while(questionIndex < topicData.qna.questions.length) {
            const question = topicData.qna.questions[questionIndex];
            if(question.oldQuestionId === questionId)
                return questionIndex;
            questionIndex ++;
        }

        return -1;
    };

    const resolveNextQuestion = (questionIndex: number | null = null, jumpQuestionId: number | null = null): number | null => {
        //console.log('========================================================================');
        //console.log('Getting next question for question index', questionIndex,'and jump ID', jumpQuestionId);
        
        if(!topicData) {
            //console.log('Topic is undefined; Ending QnA');
            props.setRatingEnd(true);
            return null;
        }

        if (jumpQuestionId !== null) {
            //console.log('Jump detected');
            const nextQuestionIndex = getQuestionIndex(jumpQuestionId);
            if(nextQuestionIndex >= 0) {
                //console.log('Jump destination exists; Jumping to', nextQuestionIndex);
                return nextQuestionIndex;
            } else {
                //console.log('Jumping to nonexistent question; Ending QnA');
                props.setRatingEnd(true);
                return null;
            }
        }

        const getNextQuestion = (questionIndex: number | null): number | null => {

            if(questionIndex === topicData.qna.questions.length - 1) {
                //console.log('This is last question; Ending QnA');
                props.setRatingEnd(true);
                return null;
            }

            const nextQuestionIndex = questionIndex === null ? 0 : questionIndex + 1;
            const nextQuestion = topicData.qna.questions[nextQuestionIndex];
            //console.log('Maybe next question is', nextQuestionIndex);

            if(!nextQuestion) {
                //console.log('Next question index not found; Ending QnA');
                props.setRatingEnd(true);
                return null;
            }

            if(nextQuestion.questionType === 'location-request') {
                if(geolocationConsent !== 'prompt') {
                    return getNextQuestion(nextQuestionIndex);
                }
            }

            if(nextQuestion.isJumpOnly) {
                //console.log('Skipping jumpOnly question', nextQuestionIndex);
                return getNextQuestion(nextQuestionIndex);
            }

            if(nextQuestion.visibleFromScore !== null && nextQuestion.visibleToScore !== null && props.ratingValue !== null) {
                const lowerBound = nextQuestion.visibleFromScore * 10;
                const upperBound = nextQuestion.visibleToScore * 10;

                if(props.ratingValue > lowerBound && props.ratingValue <= upperBound) {
                    //console.log(`Vote value is more than ${lowerBound} and less than or equal to ${upperBound}. Moving to question ${nextQuestionIndex}`);
                    return nextQuestionIndex;
                } else {
                    //console.log('Skipping question', nextQuestionIndex, 'because vote is out of bounds');
                    return getNextQuestion(nextQuestionIndex);
                }
            }

            //console.log('Moving to question', nextQuestionIndex);
            return nextQuestionIndex;
        };

        return getNextQuestion(questionIndex);
    };
 
    const [state, setState] = useState<QuestionAndAnswerState>(() => ({
        qnaAnimationState: QnAAnimationState.ContentPanelFadeIn,
        questionIndex: resolveNextQuestion() ?? 0,
        questionTime: 0
    }));

    const question = topicData?.qna.questions[state.questionIndex ?? 0];
    const questionId = question?.questionId;

    useEffect(() => {

        qnaContainerApi.start({
            opacity: 1,
            config: { duration: 400 },
            onRest: () => {
                setState({
                    ...state,
                    questionTime: Date.now(),
                    qnaAnimationState: question?.questionType === 'iframe'
                        ? QnAAnimationState.AnswersFadeIn
                        : QnAAnimationState.TypeTitleText
                });
            }
        });

    }, [state.questionIndex]);

    const onSubmitAnswer: SubmitAnswerFunction = async (jumpQuestionId: number | null = null) => {
        props.firstQuestionAnswered();

        qnaContainerApi.start({
            opacity: 0,
            config: { duration: 200 },
            onRest: () => {
                questionMediaApi.set({ opacity: 0 });

                setState({
                    ...state,
                    questionIndex: resolveNextQuestion(state.questionIndex, jumpQuestionId) ?? 0,
                    qnaAnimationState: QnAAnimationState.ContentPanelFadeIn
                });
            }
        });

        /*if (!question) return;
        
        let questionText = question.questionText;
        let answerText = answer;

        if(!link)
            MatomoTracker.trackEvent({
                category: 'qna', 
                action: 'submit-answer',
                documentTitle: topicData?.topic.name,
                href: `https://kazva.bg/${params.topicHash}`,
                customDimensions: [
                    { id: 3, value: questionText },
                    { id: 4, value: answerText},
                    { id: 5, value: '' }
                ]
            });*/
    };

    const submitIframeAnswer = async () => {
        await axios.post(`${apiUrl}/rating-answer/${topicData?.topic.id}/iframe`, {
            ratingId: props.ratingId,
            questionnaireId: question?.questionnaireId,
            questionId: question?.questionId,
            preciseCoordinates: preciseCoordinates,
            timeToAnswerMs: !state.questionTime ? 0 : Date.now() - state.questionTime,
            lang: i18n.language,
            answerText: 'iframe_continue'
        });

        onSubmitAnswer(null);

        MatomoTracker.trackEvent({
            category: 'qna', 
            action: 'submit-answer',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${params.topicHash}`,
            customDimensions: [
                { id: 3, value: question?.questionText ?? '' },
                { id: 4, value: 'iframe_continue'},
                { id: 5, value: '' }
            ]
        });
    }

    const questionTitle = topicUtils.getQuestionText(topicData, questionId ?? 0, i18n.language);

    const getQuestionSubtitle = () =>{
        let subtitle = t('qna_question_subtitle_other');

        if(question?.questionType === 'open-ended') {
            subtitle = t('qna_question_subtitle_open_ended');
        } else if(question?.questionType === 'single-choice') {
            subtitle = t('qna_question_subtitle_single_choice');
        } else if(question?.questionType === 'multiple-choice') {
            if(!question.answerLimit || question.answerLimit === question?.answers.length) {
                subtitle = t('qna_question_subtitle_multiple_choice');
            } else {
                subtitle = t('qna_question_subtitle_limited_choice', { answerLimit: question.answerLimit });
            }
        }

        return (
            <p className='auxiliary' style={{ marginTop: '10px' }}>
                {fixBold(subtitle)}
            </p>
        );
    }

    const getMediaUrl = () => {
        if (!question)
            return null;
        if (question.mediaUrl)
            return question.mediaUrl;
        return null;
    };

    const mediaUrl = getMediaUrl();

    const fadeInQna = (question: Question) => {
        setState({ ...state, qnaAnimationState: QnAAnimationState.AnswersFadeIn });
        if(question.questionType === 'dropdown')
            dropdownAnswerApi.start({ opacity: 1, config: { duration: 400 }, });
    };

    const fadeInSubtitle = ()=>
        setState({ ...state, qnaAnimationState: QnAAnimationState.TypeSubtitleText });
    

    const renderClientName = () => {
        const currentLanguage = i18n.language;

        let clientDisplayName;

        if(currentLanguage === 'bg') {
            clientDisplayName = topicData?.entity.displayNameBg;
        } else {
            clientDisplayName = topicData?.entity.displayNameEn;
        }

        if(!clientDisplayName)
            return null;

        return (
            <p className='auxiliary' style={{ margin: '0 0 10px 0' }}>
                ℹ️ {t('slider_saying_to')} <b>{clientDisplayName}</b>
            </p>
        );
    };

    return (!question ? null :
        <QuestionContext.Provider value={{
            question: question,
            onSubmitAnswer,
            questionTime: state.questionTime,
            ratingId: props.ratingId,
        }}>
            <animated.div style={qnaContainerStyle}>
                <div style={{ margin: '0 0 20px 0' }}>
                    <StyledContentPanel>
                        {renderClientName()}
                        {question.questionType === 'iframe'
                            ?
                            <>
                                <h1>{questionTitle}</h1>
                                <IframeAnswer link={question.answers[0].link} height={question.answers[0].height ?? undefined} />
                            </>
                            : <TypingTextV2
                                active={state.qnaAnimationState === QnAAnimationState.TypeTitleText}
                                delay={10}
                                reset={state.qnaAnimationState === QnAAnimationState.ContentPanelFadeIn}
                                onComplete={() => {
                                    if (mediaUrl) {
                                        setState({ ...state, qnaAnimationState: QnAAnimationState.MediaFadeIn });
                                        questionMediaApi.start({
                                            opacity: 1,
                                            config: { duration: 400 },
                                            onRest: () => {
                                                setState({ ...state, qnaAnimationState: QnAAnimationState.MediaPlay });
                                            }
                                        });
                                    } else {
                                        fadeInSubtitle();
                                    }
                                }}
                            >
                                {questionTitle}
                            </TypingTextV2>
                        }
                        {!mediaUrl ? null :
                            <animated.div style={{ 
                                marginTop: '20px',
                                ...questionMediaStyle 
                            }}>
                                <DisplayMedia
                                    shouldPlay={state.qnaAnimationState === QnAAnimationState.MediaPlay}
                                    mediaUrl={mediaUrl}
                                    onAfterOneSecond={() => { 
                                        if(state.qnaAnimationState === QnAAnimationState.MediaPlay)
                                            fadeInSubtitle();
                                    }}
                                    onMediaEnded={() => {
                                        if(state.qnaAnimationState === QnAAnimationState.MediaPlay)
                                            fadeInSubtitle();
                                    }}
                                />
                            </animated.div>
                        }
                        {question.questionType !== 'dropdown' ? null :
                            <animated.div style={{ margin: '40px 0px 20px 0', ...dropdownAnswerStyle }}>
                                <DropdownAnswer />
                            </animated.div>
                        }
                        {question.questionType === 'iframe' ? null
                            : <TypingTextV2 
                                active={(state.qnaAnimationState === QnAAnimationState.TypeSubtitleText)}
                                delay={10}
                                reset={state.qnaAnimationState === QnAAnimationState.ContentPanelFadeIn}
                                onComplete={() => fadeInQna(question)}
                            >
                                {getQuestionSubtitle()}
                            </TypingTextV2>
                    }
                    </StyledContentPanel>
                </div>
                {!(state.qnaAnimationState === QnAAnimationState.AnswersFadeIn || state.qnaAnimationState === QnAAnimationState.QuestionFadeOut ) ? null
                    : question.questionType === 'iframe' ?
                        question.answers.length > 1 && question.answers.slice(1).some((answer: Answer) => !!answer.answerText.trim())
                            ? <SingleChoiceAnswer />
                            : <StyledButton onClick={submitIframeAnswer}>
                                {t('button_continue')}
                            </StyledButton>
                    : question.questionType === 'dropdown' 
                        ? null
                    : question.questionType === 'location-request'
                        ? <RequestLocation />
                    : question.questionType === 'open-ended'
                        ? <OpenEndedAnswer />
                    : question.questionType === 'multiple-choice'
                        ? <MultipleChoiceAnswer />
                    : question.questionType === 'single-choice'
                        ? <SingleChoiceAnswer />
                        : null
                }
            </animated.div>
        </QuestionContext.Provider>
    );
};

export default QuestionAndAnswer;